import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "../components/common/Button";
import { useRegistrationFlowStore } from "../stores/registrationFlow.store";
import { useEffect, useState } from "react";
import { useAuthStore } from "../stores/auth.store";

const PageContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  text-align: center;
  margin: 48px 0;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  text-transform: uppercase;
`;

const Message = styled.p`
  font-size: 16px;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const ConfirmationIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      fill="currentColor"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      fill="currentColor"
    />
  </svg>
);

type ConfirmationState = "loading" | "success" | "expired" | "already_active";

export const ConfirmRegistration = () => {
  const navigate = useNavigate();
  const { setFirstRegistration } = useRegistrationFlowStore();
  const { authUser, isLoading: authLoading } = useAuthStore();
  const [state, setState] = useState<ConfirmationState>("loading");

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    const checkConfirmationStatus = () => {
      // Parse error parameters from URL hash and query string
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const queryParams = new URLSearchParams(window.location.search);

      const errorFromHash = hashParams.get("error");
      const errorFromQuery = queryParams.get("error");
      const hasError = errorFromHash || errorFromQuery;

      // Check authentication status from auth store
      if (authUser && !hasError) {
        // Success case: authenticated and no errors
        setState("success");
        setFirstRegistration(true);
      } else if (authUser && hasError) {
        // Already authenticated but link has error (clicked again)
        setState("already_active");
      } else if (!authUser && hasError) {
        // Not authenticated and has error (expired/invalid link)
        setState("expired");
      } else {
        // Not authenticated and no error (shouldn't happen, but treat as expired)
        setState("expired");
      }
    };

    checkConfirmationStatus();
  }, [authUser, authLoading, setFirstRegistration]);

  const handleGoToFamilyMembers = () => {
    navigate("/account/members", { state: { enablePayment: true } });
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleGoToAccount = () => {
    navigate("/account");
  };

  if (state === "loading") {
    return (
      <PageContainer>
        <ContentContainer>
          <Message>CARICAMENTO...</Message>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (state === "success") {
    return (
      <PageContainer>
        <ContentContainer>
          <IconWrapper>
            <ConfirmationIcon />
          </IconWrapper>
          <Title>EMAIL CONFERMATA</Title>
          <Message>
            LA TUA EMAIL È STATA CONFERMATA CON SUCCESSO. PROSEGUI PER AGGIUNGERE
            MEMBRI ALLA TUA FAMIGLIA E PAGARE L' ABBONAMENTO ANNUALE.
          </Message>
          <Button onClick={handleGoToFamilyMembers} fullWidth>
            VAI AVANTI
          </Button>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (state === "already_active") {
    return (
      <PageContainer>
        <ContentContainer>
          <IconWrapper>
            <ConfirmationIcon />
          </IconWrapper>
          <Title>ACCOUNT GIÀ ATTIVO</Title>
          <Message>
            IL TUO ACCOUNT È GIÀ STATO CONFERMATO E ATTIVATO. NON È NECESSARIA
            NESSUNA ULTERIORE AZIONE.
          </Message>
          <Button onClick={handleGoToAccount} fullWidth>
            VAI AL TUO ACCOUNT
          </Button>
        </ContentContainer>
      </PageContainer>
    );
  }

  // state === "expired"
  return (
    <PageContainer>
      <ContentContainer>
        <IconWrapper>
          <ErrorIcon />
        </IconWrapper>
        <Title>LINK SCADUTO O NON VALIDO</Title>
        <Message>
          IL LINK DI CONFERMA È SCADUTO O NON È PIÙ VALIDO. I LINK DI CONFERMA
          EMAIL SCADONO DOPO 24 ORE.
          <br /><br />
          OPPURE HAI GIÀ CONFERMATO IL TUO ACCOUNT IN PRECEDENZA. NESSUN PROBLEMA, PROVA AD ACCEDERE CON LE TUE CREDENZIALI.
        </Message>
        <Button onClick={handleGoToLogin} fullWidth>
          VAI AL LOGIN
        </Button>
      </ContentContainer>
    </PageContainer>
  );
};
